import { useEffect, useMemo, useState } from 'react'
import { Search } from '@carbon/icons-react'
import { useRecoilState } from 'recoil'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogCloseButton } from '../Shadcn/Dialog'
import { Typography } from '../Shadcn/Typography'
import { Button } from '../Shadcn/Button'
import { Checkbox } from '../Shadcn/Checkbox'
import { Input } from '../Shadcn/Input'
import { markerFilterDataState } from '../../states'
import { useCIFService } from '../../services'
import { cn } from '../../lib/utils'


interface MarkerFilterModalProps {
    isOpen: boolean
    onClose: () => void
    onConfirm?: () => void
    layerKey: string
    layerTitle: string
}

export const MarkerFilterModal = ({ isOpen, onClose, onConfirm, layerKey, layerTitle }: MarkerFilterModalProps) => {
    const cifService = useCIFService()
    const [markerFilterData, setMarkerFilterData] = useRecoilState(markerFilterDataState)
    const filters: any[] = markerFilterData[layerKey]?.filters ?? []

    const [searchTerm, setSearchTerm] = useState('')
    const [tempTagged, setTempTagged] = useState<Set<string>>(new Set())
    const [hoveredKey, setHoveredKey] = useState<string | null>(null)

    useEffect(() => {
        if (isOpen) {
            setTempTagged(new Set(filters.filter((f: any) => f.is_tagged).map((f: any) => f.key)))
            setSearchTerm('')
        }
    }, [isOpen])

    const filteredFilters = useMemo(() => {
        if (!searchTerm.trim()) return filters
        const q = searchTerm.toLowerCase()
        return filters.filter((f: any) =>
            f.overlay?.toLowerCase().includes(q) ||
            f.sub_heading?.toLowerCase().includes(q)
        )
    }, [filters, searchTerm])

    const handleToggle = (key: string) => {
        setTempTagged(prev => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })
    }

    const handleConfirm = () => {
        const selectedKeys = Array.from(tempTagged)
        if (selectedKeys.length > 0) {
            cifService.tagMarkerSliders(selectedKeys).catch(() => {})
        }

        setMarkerFilterData(prev => ({
            ...prev,
            [layerKey]: {
                ...prev[layerKey],
                filters: filters.map((f: any) => ({ ...f, is_tagged: tempTagged.has(f.key) })),
            },
        }))

        onClose()
        onConfirm?.()
    }

    return (
        <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
            <DialogContent className="w-[min(90vw,36rem)] max-h-[80vh] p-0 m-0 min-h-auto flex flex-col">
                <DialogHeader className="border-b border-border shrink-0">
                    <DialogTitle>
                        Add Filter for {layerTitle}
                    </DialogTitle>
                    <DialogCloseButton />
                </DialogHeader>

                {/* search */}
                <div className="px-4 py-3 border-b shrink-0">
                    <Input
                        size="small"
                        placeholder="Search"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        leftIcon={<Search size={16} />}
                        className="w-full bg-epic-neutral-2 border-transparent shadow-none hover:border-transparent focus:border-transparent focus:ring-0"
                    />
                </div>

                {/* filter list */}
                <div className="flex-1 overflow-y-auto p-[1.125rem] flex flex-col gap-3">
                    {filteredFilters.map((item: any) => {
                        const isChecked = tempTagged.has(item.key)
                        const isCategorical = item.type === 'categorical_variable'
                        const isDate = item.type === 'date'
                        const isNa = item.is_na || (item.max == null && item.type !== 'slider' && !isDate)
                        return (
                            <div
                                key={item.key}
                                onClick={() => handleToggle(item.key)}
                                onMouseEnter={() => setHoveredKey(item.key)}
                                onMouseLeave={() => setHoveredKey(null)}
                                className={cn(
                                    'flex flex-col gap-1 p-3 rounded-[var(--semantic-rounded-md)] border border-dashed cursor-pointer transition-colors duration-150',
                                    isChecked ? 'border-brand bg-btn-secondary-bg' : 'border-border hover:border-brand/50'
                                )}
                            >
                                {/* title row */}
                                <div className="flex items-center gap-2">
                                    <Checkbox
                                        id={`mf-${item.key}`}
                                        checked={isChecked}
                                        onCheckedChange={() => handleToggle(item.key)}
                                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                        className="shrink-0"
                                    />
                                    <Typography variant="b1" weight="medium" className="text-start text-[0.75rem] truncate flex-1">
                                        {item.overlay}
                                    </Typography>
                                    {isCategorical && (
                                        <span className="text-[0.625rem] text-muted-foreground border border-border rounded px-1.5 py-0.5 shrink-0 leading-none">
                                            categorical variable
                                        </span>
                                    )}
                                    {/* {isDate && (
                                        <span className="text-[0.625rem] text-muted-foreground border border-border rounded px-1.5 py-0.5 shrink-0 leading-none">
                                            date range
                                        </span>
                                    )} */}
                                    {isNa && (
                                        <span className="text-[0.625rem] bg-[#FFF3CD] text-[#856404] rounded px-1.5 py-0.5 font-medium shrink-0 leading-none">
                                            NA
                                        </span>
                                    )}
                                    {(hoveredKey === item.key || isChecked) && item.metadata?.unit && (
                                        <span className="px-1.5 py-0.5 rounded text-[0.5625rem] font-medium whitespace-nowrap bg-background border border-border text-foreground font-mono shrink-0">
                                            {item.metadata.unit}
                                        </span>
                                    )}
                                </div>
                                {/* sub-heading row */}
                                {/* <div className="flex items-end justify-between gap-2 min-h-[1.25rem]">
                                    {item.sub_heading && (
                                        <Typography variant="b2" className="text-muted-foreground text-start text-[0.65625rem] leading-[1.4] line-clamp-2 flex-1">
                                            {item.sub_heading}
                                        </Typography>
                                    )}
                                </div> */}
                            </div>
                        )
                    })}
                </div>

                {/* footer */}
                <div className="flex items-center justify-end gap-2 border-t px-4 py-3 shrink-0">
                    <Button variant="outline" size="small" rounded="full" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        size="small"
                        rounded="full"
                        onClick={handleConfirm}
                        // disabled={tempTagged.size === 0}
                    >
                        Apply
                        ({tempTagged.size})
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    )
}
